<template>
  <div class="account__subcontainer">
    <h1 class="h2 account__title mb-2">
      {{ $t(activateMode ? 'activate.title' : 'register.title') }}
    </h1>

    <p class="text-color-secondary-2 text-small mb-2">
      {{ $t(activateMode ? 'activate.p' : 'register.p') }}
    </p>

    <form
      :id="`account-${activateMode ? 'activate' : 'register'}`"
      action="#"
      novalidate
      @submit.prevent="step === 1 ? nextStep() : createAccount()"
    >
      <div class="account__input account__input--email mb-2">
        <form-input
          id="input-email-register"
          :value="email"
          type="email"
          :label="$t('account.labels.email')"
          label-visible
          :placeholder="$t('account.placeholder.email')"
          readonly
        >
          <a
            v-if="!activateMode && step === 1"
            href="#"
            class="input__icon"
            @click.prevent="changeMail"
          >
            <span class="visually-hidden">{{ $t('action.edit') }}</span>
            <iconEdit />
          </a>
        </form-input>
      </div>

      <transition name="fade" mode="out-in">
        <div v-if="step === 1" key="step1">
          <form-input
            id="input-email-register-pwd"
            ref="inputPassword"
            v-model="password"
            :type="passwordType"
            :label="$t('account.labels.password')"
            label-visible
            :placeholder="$t('login.placeholder.password')"
            autocomplete="current-password"
            :input-errors="apiErrors"
            required
            first-submit
            @input="apiErrors.length > 0 ? checkPassword() : ''"
          >
            <a
              href="#"
              class="input__icon"
              @click.prevent="changeVisibilityPassword"
            >
              <icon
                :name="passwordVisible ? 'eye-slash' : 'eye'"
                :alt="
                  $t(
                    passwordVisible
                      ? 'action.hidePassword'
                      : 'action.seePassword'
                  )
                "
              />
            </a>
          </form-input>

          <div class="mt-2">
            <primary-button
              type="submit"
              :text="$t('register.nextButton')"
              align="center"
              :loading="loading"
              :disabled="loading === true"
              with-arrow
            />
          </div>

          <div v-if="unexpectedError !== false" class="invalid__field">
            {{ $t('register.error.unexpectedError') }}
          </div>
        </div>

        <div v-else-if="step === 2" key="step2">
          <label for="gender-Homme" class="text-small mb-0-2 pl-0-6">{{
            $t('account.labels.gender')
          }}</label>

          <input-radios
            id="gender"
            v-model="gender"
            :values="[
              { value: 'Homme', label: $t('register.men') },
              { value: 'Femme', label: $t('register.women') },
            ]"
            required
            class="pl-0-6 mb-1-5"
          />

          <div class="d-flex justify-space-between mb-1">
            <div>
              <form-input
                id="given-name"
                v-model="firstName"
                :label="$t('account.labels.firstname')"
                label-visible=""
                :placeholder="$t('register.placeholder.firstName')"
                autocomplete="given-name"
                :input-errors="!validFirstName ? [{ code: 'firstName' }] : []"
                class="mr-1 flex-grow-1"
                required
                first-submit
                @input-event="updateFirstName"
              />
            </div>

            <div>
              <form-input
                id="family-name"
                v-model="lastName"
                :label="$t('account.labels.lastname')"
                label-visible
                :placeholder="$t('register.placeholder.lastName')"
                autocomplete="family-name"
                :input-errors="!validLastName ? [{ code: 'lastName' }] : []"
                class="ml-1 flex-grow-1"
                required
                first-submit
                @input-event="updateLastName"
              />
            </div>
          </div>

          <div class="mb-2">
            <form-input
              id="input-date"
              v-model="birthday"
              type="date"
              :label="$t('account.labels.birthday')"
              label-visible
              autocomplete="bday"
              class="flex-grow-1"
            />
          </div>

          <input-checkbox
            v-if="!hideAcceptMarketing"
            id="news"
            v-model="acceptsMarketing"
            name="news"
            class="text-small"
          >
            {{ $t('register.newsText') }}
          </input-checkbox>

          <div class="mt-2">
            <PrimaryButton
              type="submit"
              :text="
                $t(activateMode ? 'activate.sendButton' : 'register.sendButton')
              "
              align="center"
              :loading="loading"
              :success="success"
              :disabled="loading === true || success === true"
              with-arrow
            />
          </div>

          <div v-if="unexpectedError !== false" class="invalid__field">
            {{ $t('register.error.unexpectedError') }}
          </div>
        </div>
      </transition>
    </form>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapGetters } from 'vuex'
import services from '@/services'

import FormInput from '@/components/form/Input'
import Icon from '@/components/icons/Icon'
import InputRadios from '@/components/form/InputRadios'
import InputCheckbox from '@/components/form/InputCheckbox'
import PrimaryButton from '@/components/buttons/Primary'
import IconEdit from '@/components/icons/Edit'

import Customer from '@/entities/Customer'

export default {
  name: 'Register',
  components: {
    FormInput,
    InputRadios,
    InputCheckbox,
    PrimaryButton,
    IconEdit,
    Icon,
  },
  props: {
    email: { type: String, required: true },
    id: { type: String, default: '' },
    activationToken: { type: String, default: '' },
    activateMode: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      password: '',
      passwordVisible: false,
      firstName: '',
      lastName: '',
      birthday: '',
      acceptsMarketing: false,
      step: 1,
      gender: '',
      loading: false,
      validPassword: true,
      validFirstName: true,
      validLastName: true,
      unexpectedError: false,
      hideAcceptMarketing: false,
      success: false,
    }
  },

  head() {
    return {
      title: `${
        this.activateMode
          ? this.$t('activate.title')
          : this.$t('register.title')
      } - Asphalte`,
    }
  },

  computed: {
    ...mapGetters({
      apiErrors: 'auth/errors',
      customerAccessToken: 'auth/token',
      customer: 'auth/customer',
      authenticated: 'auth/authenticated',
    }),

    passwordType() {
      return this.passwordVisible === true ? 'text' : 'password'
    },
  },

  mounted() {
    history.pushState(null, null, location.href)
    window.onpopstate = () => {
      this.resetState()
      if (this.step === 2) {
        this.step = 1
      } else if (this.step === 1) {
        this.$emit('go-back-event')
      }
    }
    this.$track.page(
      { title: this.$t('register.title') },
      this.$route,
      this.$nuxt.context.from
    )
  },

  methods: {
    ...mapActions({
      setConnectedEmail: 'subscriptions/setConnectedEmail',
      createCustomer: 'auth/createCustomer',
      activateCustomer: 'auth/activateCustomer',
    }),

    ...mapMutations({
      setApiErrors: 'auth/ERRORS',
      clearApiErrors: 'auth/CLEAR_ERRORS',
      setCustomer: 'auth/CUSTOMER',
    }),

    changeMail() {
      this.resetState()
      this.step = 1
      this.$emit('go-back-event')
    },

    resetState() {
      this.loading = false
      this.validPassword = true
      this.validFirstName = true
      this.validLastName = true
      this.unexpectedError = false
      this.success = false
    },

    checkErrors(customerResponse) {
      const apiErrors = []
      if (
        customerResponse.customerUserErrors &&
        customerResponse.customerUserErrors.length > 0
      ) {
        customerResponse.customerUserErrors.forEach((err) => {
          apiErrors.push(err)
        })
        this.setApiErrors(apiErrors)
      } else if (customerResponse.customer !== null) {
        this.setCustomer(new Customer(customerResponse.customer))
      }
    },

    transformId(id, mode) {
      if (mode === 'storefront') {
        const idArray = id.split('/')
        return idArray[idArray.length - 1]
      }
    },

    updateFirstName(value) {
      this.firstName = value
    },

    updateLastName(value) {
      this.lastName = value
    },

    nextStep() {
      this.clearApiErrors()
      this.checkPassword()
      if (this.validPassword === true) {
        this.loading = true

        this.$track.password()
        if (
          this.activateMode === true &&
          this.activationToken !== null &&
          this.id !== null
        ) {
          this.activateCustomer({
            id: this.id,
            activationToken: this.activationToken,
            password: this.password,
          })
            .then(() => {
              if (this.authenticated) {
                this.setConnectedEmail(this.email)
              }

              this.step = 2
              this.firstName = this.customer.firstName
              this.lastName = this.customer.lastName
              if (this.customer.acceptsMarketing) {
                this.hideAcceptMarketing = true
              }
              this.acceptsMarketing = this.customer.acceptsMarketing
              // this.email = this.customer.email
            })
            .catch((err) => {
              this.$logError.captureException(err)
              this.unexpectedError = true
            })
            .finally(() => {
              this.loading = false
            })
        } else {
          this.createCustomer({
            email: this.email,
            password: this.password,
          })
            .then(() => {
              if (this.authenticated) {
                this.setConnectedEmail(this.email)
                this.step = 2
              }
            })
            .catch((err) => {
              this.$logError.captureException(err)
              this.unexpectedError = true
            })
            .finally(() => {
              this.loading = false
            })
        }
      }
    },

    createAccount(event) {
      if (event) {
        event.preventDefault()
      }
      this.checkFirstName()
      this.checkLastName()

      this.clearApiErrors()
      this.$nextTick(() => {
        if (this.validFirstName === true && this.validLastName === true) {
          this.loading = true

          const inputCustomer = {
            email: this.email,
            firstName: this.firstName,
            lastName: this.lastName,
            acceptsMarketing: this.acceptsMarketing,
          }

          // TODO: voir si utile de bouger dans l'action store
          services.customerService
            .updateCustomer(this.customerAccessToken.accessToken, inputCustomer)
            .then((customerUpdate) => {
              this.checkErrors(customerUpdate)
              if (this.apiErrors.length === 0) {
                const id = this.transformId(this.customer.id, 'storefront')
                const params = {
                  id,
                  gender: this.gender,
                  birthday: this.birthday,
                }

                if (this.acceptsMarketing && !this.hideAcceptMarketing) {
                  this.subscribeToNewsletter()
                }

                this.$track.signUp(this.customer)

                services.accountManagerService
                  .updateCustomer(params)
                  .then(() => {
                    this.$emit('redirect-event')
                  })
                  .catch((err) => {
                    this.$logError.captureException(err)
                    this.unexpectedError = true
                  })
                  .finally(() => {
                    this.loading = false
                  })
              }
            })
            .catch((err) => {
              // sentry.sendErrorToSentry(err, 'storefront', 'updateCustomer')

              if (err) {
                this.$logError.captureException(err, {
                  tags: {
                    function: 'createAccount',
                  },
                })
              }

              this.unexpectedError = true
              this.loading = false
            })
        }
      })
    },

    checkPassword() {
      if (this.password === '' || this.password === null) {
        this.validPassword = false
      } else {
        this.validPassword = this.password.length >= 6

        if (!this.validPassword) {
          this.setApiErrors([{ code: 'passwordLength' }])
        }
      }
    },

    checkFirstName() {
      if (this.firstName === '' || this.firstName === null) {
        this.validFirstName = false
      } else {
        this.validFirstName = this.firstName.length >= 2
      }
    },

    checkLastName() {
      if (this.lastName === '' || this.lastName === null) {
        this.validLastName = false
      } else {
        this.validLastName = this.lastName.length >= 2
      }
    },

    changeVisibilityPassword() {
      this.passwordVisible = !this.passwordVisible
    },

    subscribeToNewsletter() {
      const locale = this.$i18n.locales.find((locale) => {
        return locale.code === this.$i18n.locale
      })

      const customerOptions = {
        email: this.email,
        first_name: this.firstName,
        last_name: this.lastName,
        locale: locale.code.substring(0, 2),
      }

      const list = locale[`klaviyoNewsletter-${this.$route.params.context}`]
      const techOptions = {
        vendor: 'klaviyo-subscribe',
        action: 'subscribe',
        list_id: list,
        signup_location: 'account',
        'Accepts Marketing': 'true',
        account_subscription: 'true',
      }

      const options = { ...customerOptions, ...techOptions }

      // TODO TEMP : track users who subscribe NL
      const optionsTrack = {
        vendor: 'klaviyo-subscribe',
        action: 'track',
        email: this.email,
        event_name: 'try-to-subscribe-to-list',
        'event_properties[location]': 'account',
        'event_properties[list]': list,
        'event_properties[context]': this.$route.params.context,
        'event_properties[locale]': locale.code.substring(0, 2),
      }

      wb_apihelper(optionsTrack, (err) => {
        if (err) {
          this.$logError.captureException(err)
        }
      })
      // TODO END TEMP

      wb_apihelper(options, function (err) {
        if (err) {
          // sentry.sendErrorToSentry(err, 'storefront', 'subscribeNewsletter')
          this.$logError.captureException(err)
        }
      })
      /* eslint-enable no-undef */
    },
  },
}
</script>

<style lang="scss">
.circle__black {
  background: RGB(0 0 0);
  border: 1px solid RGB(0 0 0);
  height: 8px;
  width: 8px;
  border-radius: 50%;
}

.circle__white {
  border: 1px solid RGB(202 202 202);
  height: 8px;
  width: 8px;
  border-radius: 50%;
}

.divider {
  border-bottom: 1px solid RGB(202 202 202);
  height: 1px;
  width: 15px;
  align-self: center;
  margin-right: 4px;
  margin-left: 4px;
}

.divider__black {
  border-bottom: 1px solid RGB(0 0 0);
  height: 1px;
  width: 15px;
  align-self: center;
  margin-right: 4px;
  margin-left: 4px;
}
</style>
